import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import "./user-management.css";

type UserRole =
  | "super_master"
  | "platform_master"
  | "commercial_manager"
  | "commercial";
type PlatformUser = {
  user_id: string;
  display_name: string | null;
  full_name: string | null;
  personal_email: string | null;
  email: string | null;
  phone: string | null;
  alias: string | null;
  role_key: UserRole;
  permissions: string[];
  active: boolean;
  password_change_required: boolean;
  first_access_completed_at: string | null;
  created_at: string;
};
type StandUser = {
  membership_id: string;
  user_id: string;
  display_name: string;
  full_name: string | null;
  personal_email: string | null;
  email: string | null;
  phone: string | null;
  alias: string | null;
  organization_id: string;
  organization_name: string;
  role_key: string;
  status: string;
};
type UserAccessEvent = {
  id: number;
  event_type: string;
  message: string | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  created_at: string;
  actor_display_name: string;
  scope: string;
};
type Organization = { id: string; name: string; commercial_status: string };
const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const roleLabels: Record<UserRole, string> = {
  super_master: "Super Master",
  platform_master: "Master",
  commercial_manager: "Gestor Comercial",
  commercial: "Comercial",
};
const standRoleLabels: Record<string, string> = {
  stand_master: "Master do stand",
  manager: "Gestor",
  sales: "Comercial",
  operations: "Operações",
  user: "Utilizador",
};
const permissionOptions = [
  {
    key: "users.manage",
    label: "Gerir utilizadores",
    detail: "Consultar, criar e editar utilizadores autorizados.",
  },
  {
    key: "commercial.manage",
    label: "Gestão comercial",
    detail: "Acompanhar equipas, leads e tarefas de toda a operação.",
  },
  {
    key: "tasks.audit",
    label: "Auditar tarefas",
    detail: "Rever desistências, reabrir tarefas e consultar histórico.",
  },
  {
    key: "plans.manage",
    label: "Gerir planos",
    detail: "Criar e alterar planos comerciais.",
  },
  {
    key: "templates.manage",
    label: "Gerir templates",
    detail: "Criar e publicar templates para stands.",
  },
  {
    key: "versions.manage",
    label: "Gerir versões",
    detail: "Preparar versões e acompanhar validações.",
  },
  {
    key: "support.manage",
    label: "Atender suporte",
    detail:
      "Consultar e responder apenas aos pedidos atribuídos ao suporte Vorno.",
  },
  {
    key: "production.publish",
    label: "Publicar em Produção",
    detail: "Permissão crítica para publicar o produto final.",
  },
];
const initial = (name: string | null, email: string | null) =>
  ((name || email || "#").trim()[0] || "#")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

export function UserManagement({
  isSuperMaster,
  canManage,
}: {
  isSuperMaster: boolean;
  canManage: boolean;
}) {
  const [scope, setScope] = useState<"platform" | "stands">("platform"),
    [users, setUsers] = useState<PlatformUser[]>([]),
    [standUsers, setStandUsers] = useState<StandUser[]>([]),
    [organizations, setOrganizations] = useState<Organization[]>([]),
    [loading, setLoading] = useState(true),
    [standLoading, setStandLoading] = useState(false),
    [letter, setLetter] = useState("all"),
    [userFilter, setUserFilter] = useState("all"),
    [standFilter, setStandFilter] = useState("all"),
    [showCreatePlatform, setShowCreatePlatform] = useState(false),
    [showCreateStand, setShowCreateStand] = useState(false),
    [selected, setSelected] = useState<PlatformUser | null>(null),
    [selectedStandUser, setSelectedStandUser] = useState<StandUser | null>(
      null,
    ),
    [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("list_platform_users");
    if (error) setMessage(error.message);
    else setUsers((data ?? []) as PlatformUser[]);
    setLoading(false);
  };
  const loadStandUsers = async () => {
    setStandLoading(true);
    const [directory, orgResult] = await Promise.all([
      supabase.rpc("list_platform_stand_users"),
      supabase
        .from("organizations")
        .select("id,name,commercial_status")
        .is("archived_at", null)
        .order("name"),
    ]);
    if (directory.error || orgResult.error) {
      setMessage(
        directory.error?.message ??
          orgResult.error?.message ??
          "Não foi possível carregar os utilizadores dos stands.",
      );
      setStandLoading(false);
      return;
    }
    setOrganizations((orgResult.data ?? []) as Organization[]);
    setStandUsers((directory.data ?? []) as StandUser[]);
    setStandLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message]);
  useEffect(() => {
    if (scope === "stands" && !organizations.length) void loadStandUsers();
  }, [scope]);
  const platformOptions = useMemo(
    () =>
      [...users].sort((a, b) =>
        (a.display_name ?? a.email ?? "").localeCompare(
          b.display_name ?? b.email ?? "",
          "pt",
        ),
      ),
    [users],
  );
  const standUserOptions = useMemo(
    () =>
      [...standUsers]
        .filter(
          (user) =>
            standFilter === "all" || user.organization_id === standFilter,
        )
        .sort((a, b) => a.display_name.localeCompare(b.display_name, "pt")),
    [standUsers, standFilter],
  );
  const filtered = useMemo(
    () =>
      platformOptions.filter(
        (user) =>
          (userFilter === "all" || user.user_id === userFilter) &&
          (letter === "all" ||
            initial(user.display_name, user.email) === letter),
      ),
    [platformOptions, userFilter, letter],
  );
  const filteredStandUsers = useMemo(
    () =>
      standUserOptions.filter(
        (user) =>
          (userFilter === "all" || user.membership_id === userFilter) &&
          (letter === "all" ||
            initial(user.display_name, user.email) === letter),
      ),
    [standUserOptions, userFilter, letter],
  );
  const changeScope = (next: "platform" | "stands") => {
    setScope(next);
    setLetter("all");
    setUserFilter("all");
    setStandFilter("all");
  };
  return (
    <section className="users-workspace">
      <header className="users-heading">
        <div>
          <p className="eyebrow">Gestão de acessos</p>
          <p>
            Escolha primeiro o âmbito: equipa central do Vorno Stand ou
            utilizadores de um stand.
          </p>
        </div>
        {canManage && (
          <button
            className="primary-button compact"
            onClick={() =>
              scope === "platform"
                ? setShowCreatePlatform(true)
                : setShowCreateStand(true)
            }
          >
            +{" "}
            {scope === "platform"
              ? "Criar utilizador da plataforma"
              : "Criar utilizador de stand"}
          </button>
        )}
      </header>
      {message && (
        <button
          className="notice"
          role="status"
          aria-live="polite"
          onClick={() => setMessage(null)}
        >
          {message} ×
        </button>
      )}
      <nav
        className="user-scope-tabs"
        aria-label="Âmbito dos utilizadores"
        role="tablist"
      >
        <button
          aria-selected={scope === "platform"}
          className={scope === "platform" ? "active" : ""}
          onClick={() => changeScope("platform")}
          role="tab"
        >
          <strong>Equipa Vorno Stand</strong>
          <small>
            Super Master, Masters, gestores e comerciais da plataforma.
          </small>
        </button>
        <button
          aria-selected={scope === "stands"}
          className={scope === "stands" ? "active" : ""}
          onClick={() => changeScope("stands")}
          role="tab"
        >
          <strong>Utilizadores dos stands</strong>
          <small>Masters e equipas de cada stand cliente.</small>
        </button>
      </nav>
      {scope === "platform" ? (
        <>
          <div className="scope-guidance">
            <strong>Gestão central</strong>
            <span>
              O Super Master vê toda a equipa. Masters com a permissão “Gerir
              utilizadores” podem prestar apoio dentro dos limites da sua
              função.
            </span>
          </div>
          <FilterBar
            users={platformOptions.map((user) => ({
              id: user.user_id,
              name: user.display_name ?? user.email ?? "Sem nome",
            }))}
            letter={letter}
            userFilter={userFilter}
            onLetter={setLetter}
            onUser={setUserFilter}
            count={filtered.length}
          />
          {loading ? (
            <div className="empty-state">A carregar utilizadores…</div>
          ) : (
            <PlatformList users={filtered} onSelect={setSelected} />
          )}
        </>
      ) : (
        <>
          <div className="scope-guidance">
            <strong>Apoio aos stands</strong>
            <span>
              O Master do stand gere a sua equipa; o Master autorizado do Vorno
              Stand também pode criar, editar, suspender ou remover acessos para
              prestar apoio.
            </span>
          </div>
          <FilterBar
            organizations={organizations}
            standFilter={standFilter}
            onStand={(value) => {
              setStandFilter(value);
              setUserFilter("all");
            }}
            users={standUserOptions.map((user) => ({
              id: user.membership_id,
              name: user.display_name,
            }))}
            letter={letter}
            userFilter={userFilter}
            onLetter={setLetter}
            onUser={setUserFilter}
            count={filteredStandUsers.length}
          />
          {standLoading ? (
            <div className="empty-state">
              A carregar utilizadores dos stands…
            </div>
          ) : filteredStandUsers.length ? (
            <StandList
              users={filteredStandUsers}
              onSelect={setSelectedStandUser}
            />
          ) : (
            <div className="empty-state">
              <strong>Nenhum vínculo corresponde aos filtros.</strong>
              <span>
                Escolha outro stand, letra ou utilizador. Se ainda não houver
                equipa, use “Criar utilizador de stand”.
              </span>
            </div>
          )}
        </>
      )}
      {showCreatePlatform && (
        <CreatePlatformUserDialog
          onClose={() => setShowCreatePlatform(false)}
          onCreated={() => {
            setShowCreatePlatform(false);
            setMessage(
              "Utilizador criado. No primeiro acesso será exigida uma nova palavra-passe.",
            );
            void load();
          }}
        />
      )}
      {showCreateStand && (
        <CreateStandUserDialog
          organizations={organizations}
          onClose={() => setShowCreateStand(false)}
          onCreated={() => {
            setShowCreateStand(false);
            setMessage("Utilizador criado e associado ao stand.");
            void loadStandUsers();
          }}
        />
      )}
      {selected && (
        <EditUserDialog
          user={selected}
          isSuperMaster={isSuperMaster}
          onClose={() => setSelected(null)}
          onSaved={(message) => {
            setSelected(null);
            setMessage(message);
            void load();
          }}
        />
      )}
      {selectedStandUser && (
        <EditStandUserDialog
          user={selectedStandUser}
          canManage={canManage}
          onClose={() => setSelectedStandUser(null)}
          onSaved={(message) => {
            setSelectedStandUser(null);
            setMessage(message);
            void loadStandUsers();
          }}
        />
      )}
    </section>
  );
}

function FilterBar({
  organizations,
  standFilter,
  onStand,
  users,
  letter,
  userFilter,
  onLetter,
  onUser,
  count,
}: {
  organizations?: Organization[];
  standFilter?: string;
  onStand?: (value: string) => void;
  users: { id: string; name: string }[];
  letter: string;
  userFilter: string;
  onLetter: (value: string) => void;
  onUser: (value: string) => void;
  count: number;
}) {
  return (
    <div className="guided-filters">
      {organizations && (
        <label>
          <span>Stand</span>
          <select
            value={standFilter}
            onChange={(event) => onStand?.(event.target.value)}
          >
            <option value="all">Todos os stands</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        <span>Utilizador</span>
        <select
          value={userFilter}
          onChange={(event) => onUser(event.target.value)}
        >
          <option value="all">Todos os utilizadores</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </label>
      <div className="alphabet-filter" aria-label="Filtrar pela primeira letra">
        <span>Nome iniciado por</span>
        <div>
          <button
            className={letter === "all" ? "active" : ""}
            onClick={() => onLetter("all")}
          >
            Todos
          </button>
          {alphabet.map((item) => (
            <button
              key={item}
              className={letter === item ? "active" : ""}
              onClick={() => onLetter(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
      <small>
        {count} resultado{count === 1 ? "" : "s"}
      </small>
    </div>
  );
}
function PlatformList({
  users,
  onSelect,
}: {
  users: PlatformUser[];
  onSelect: (user: PlatformUser) => void;
}) {
  return (
    <div className="users-list">
      {users.map((user) => (
        <button
          key={user.user_id}
          className={!user.active ? "inactive" : ""}
          onClick={() => onSelect(user)}
        >
          <span className="user-avatar">
            {initial(user.display_name, user.email)}
          </span>
          <span>
            <strong>{user.full_name ?? user.display_name ?? "Sem nome"}</strong>
            <small>{user.alias ? `@${user.alias}` : "Nome no sistema por definir"} · {user.email ?? "Email profissional não indicado"}</small>
            {user.personal_email && <small>Pessoal: {user.personal_email}</small>}
          </span>
          <em>{roleLabels[user.role_key]}</em>
          <span
            className={`user-state ${user.active ? "active" : "suspended"}`}
          >
            {user.active
              ? user.password_change_required
                ? "Primeiro acesso"
                : "Ativo"
              : "Suspenso"}
          </span>
          <b className="user-edit-action">Editar</b>
        </button>
      ))}
    </div>
  );
}
function StandList({
  users,
  onSelect,
}: {
  users: StandUser[];
  onSelect: (user: StandUser) => void;
}) {
  return (
    <div className="users-list stand-users-list">
      {users.map((user) => (
        <button key={user.membership_id} onClick={() => onSelect(user)}>
          <span className="user-avatar">
            {initial(user.display_name, user.email)}
          </span>
          <span>
            <strong>{user.full_name ?? user.display_name}</strong>
            <small>{user.alias ? `@${user.alias}` : "Nome no sistema por definir"} · {user.email ?? "Email profissional não indicado"}</small>
            {user.personal_email && <small>Pessoal: {user.personal_email}</small>}
          </span>
          <em>{user.organization_name}</em>
          <span
            className={`user-state ${user.status === "active" ? "active" : "suspended"}`}
          >
            {standRoleLabels[user.role_key] ?? user.role_key}
          </span>
          <b className="user-edit-action">Editar</b>
        </button>
      ))}
    </div>
  );
}
function PermissionPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="permission-picker">
      {permissionOptions.map((item) => (
        <label key={item.key}>
          <input
            type="checkbox"
            checked={value.includes(item.key)}
            onChange={() =>
              onChange(
                value.includes(item.key)
                  ? value.filter((key) => key !== item.key)
                  : [...value, item.key],
              )
            }
          />
          <span>
            <strong>{item.label}</strong>
            <small>{item.detail}</small>
          </span>
        </label>
      ))}
    </div>
  );
}

function CreatePlatformUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [role, setRole] = useState<UserRole>("commercial"),
    [permissions, setPermissions] = useState<string[]>([]),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    const { error } = await supabase.functions.invoke("manage-platform-user", {
      body: {
        display_name: form.get("system_name"),
        system_name: form.get("system_name"),
        full_name: form.get("full_name"),
        personal_email: form.get("personal_email"),
        professional_email: form.get("professional_email"),
        phone: form.get("phone"),
        alias: form.get("system_name"),
        password: form.get("password"),
        role_key: role,
        permissions,
      },
    });
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    onCreated();
  };
  return (
    <UserDialog
      title="Criar utilizador da plataforma"
      subtitle="A palavra-passe é temporária e terá de ser substituída no primeiro acesso."
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <IdentityFields />
        <label>
          <span>Função *</span>
          <select
            value={role}
            onChange={(event) => {
              setRole(event.target.value as UserRole);
              setPermissions([]);
            }}
          >
            <option value="commercial">Comercial</option>
            <option value="commercial_manager">Gestor Comercial</option>
            <option value="platform_master">Master</option>
          </select>
        </label>
        {role !== "commercial" && (
          <>
            <h3>Permissões adicionais</h3>
            <PermissionPicker value={permissions} onChange={setPermissions} />
          </>
        )}
        {error && <p className="form-error">{error}</p>}
        <DialogFooter onClose={onClose} saving={saving} />
      </form>
    </UserDialog>
  );
}
function CreateStandUserDialog({
  organizations,
  onClose,
  onCreated,
}: {
  organizations: Organization[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [role, setRole] = useState("user"),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const { error } = await supabase.functions.invoke("manage-platform-user", {
      body: {
        action: "create_stand_user",
        organization_id: form.get("organization_id"),
        display_name: form.get("system_name"),
        system_name: form.get("system_name"),
        full_name: form.get("full_name"),
        personal_email: form.get("personal_email"),
        professional_email: form.get("professional_email"),
        phone: form.get("phone"),
        alias: form.get("system_name"),
        password: form.get("password"),
        role_key: role,
        permissions: [],
      },
    });
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    onCreated();
  };
  return (
    <UserDialog
      title="Criar utilizador de stand"
      subtitle="Escolha o stand antes de definir o acesso da pessoa."
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <label>
          <span>Stand *</span>
          <select name="organization_id" required defaultValue="">
            <option value="" disabled>
              Escolher stand
            </option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </label>
        <IdentityFields />
        <label>
          <span>Função no stand *</span>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            {Object.entries(standRoleLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {error && <p className="form-error">{error}</p>}
        <DialogFooter onClose={onClose} saving={saving} />
      </form>
    </UserDialog>
  );
}
function IdentityFields() {
  return (
    <div className="identity-sections">
      <section>
        <header><strong>Informação pessoal</strong><small>Identifica inequivocamente o funcionário.</small></header>
        <div className="form-grid">
          <label><span>Nome completo *</span><input name="full_name" required autoFocus /></label>
          <label><span>Email pessoal</span><input name="personal_email" type="email" /></label>
          <label><span>Telefone</span><input name="phone" type="tel" /></label>
        </div>
      </section>
      <section>
        <header><strong>Informação profissional e acesso</strong><small>Dados apresentados no Stand e usados para iniciar sessão.</small></header>
        <div className="form-grid">
          <label><span>Nome no sistema *</span><input name="system_name" required placeholder="Ex.: joao.silva" /></label>
          <label><span>Email profissional / acesso *</span><input name="professional_email" type="email" required /></label>
          <label><span>Palavra-passe temporária *</span><input name="password" type="password" minLength={10} required /></label>
        </div>
      </section>
    </div>
  );
}
function UserDialog({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="modal-backdrop">
      <section className="user-dialog" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">Gestão de acesso</p>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
function DialogFooter({
  onClose,
  saving,
  label = "Criar utilizador",
}: {
  onClose: () => void;
  saving: boolean;
  label?: string;
}) {
  return (
    <footer>
      <button className="text-button" type="button" onClick={onClose}>
        Cancelar
      </button>
      <button className="primary-button compact" disabled={saving}>
        {saving ? "A guardar…" : label}
      </button>
    </footer>
  );
}

function PasswordResetControl({
  userId,
  membershipId,
  enabled,
  onSaved,
}: {
  userId: string;
  membershipId?: string;
  enabled: boolean;
  onSaved: (message: string) => void;
}) {
  const [open, setOpen] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [password, setPassword] = useState(""),
    [confirmation, setConfirmation] = useState("");
  if (!enabled) return null;
  const save = async () => {
    setError(null);
    if (password !== confirmation) {
      setError("As duas palavras-passe não coincidem.");
      return;
    }
    if (
      password.length < 10 ||
      !/[a-z]/.test(password) ||
      !/[A-Z]/.test(password) ||
      !/\d/.test(password) ||
      !/[^A-Za-z0-9\s]/.test(password)
    ) {
      setError(
        "Use pelo menos 10 caracteres, incluindo maiúscula, minúscula, número e símbolo.",
      );
      return;
    }
    setSaving(true);
    const { error: invokeError } = await supabase.functions.invoke(
      "manage-platform-user",
      {
        body: {
          action: "reset_user_password",
          user_id: userId,
          membership_id: membershipId,
          password,
        },
      },
    );
    setSaving(false);
    if (invokeError) {
      setError(
        invokeError.message ||
          "Não foi possível definir a palavra-passe temporária.",
      );
      return;
    }
    setOpen(false);
    setPassword("");
    setConfirmation("");
    onSaved(
      "Palavra-passe temporária definida. Será obrigatório alterá-la no próximo acesso.",
    );
  };
  return (
    <section className="password-reset-control">
      <div>
        <strong>Palavra-passe</strong>
        <span>
          A palavra-passe atual nunca pode ser consultada. Pode substituí-la por
          uma temporária para devolver o acesso ao utilizador.
        </span>
      </div>
      {!open ? (
        <button
          className="secondary-button"
          type="button"
          onClick={() => setOpen(true)}
        >
          Alterar palavra-passe
        </button>
      ) : (
        <div className="password-reset-form">
          <label>
            <span>Nova palavra-passe temporária *</span>
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              autoComplete="new-password"
            />
          </label>
          <label>
            <span>Confirmar palavra-passe *</span>
            <input
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              type="password"
              autoComplete="new-password"
            />
          </label>
          <small>
            Mínimo de 10 caracteres, com maiúscula, minúscula, número e símbolo.
          </small>
          {error && <p className="form-error">{error}</p>}
          <div>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="primary-button compact"
              type="button"
              disabled={saving}
              onClick={() => void save()}
            >
              {saving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function UserAccessHistory({userId, organizationId}:{userId:string;organizationId?:string}) {
  const [events,setEvents]=useState<UserAccessEvent[]>([]),[loading,setLoading]=useState(true),[error,setError]=useState<string|null>(null);
  useEffect(()=>{setLoading(true);void supabase.rpc("list_user_access_history",{p_user_id:userId,p_organization_id:organizationId??null}).then(({data,error:historyError})=>{setLoading(false);if(historyError){setError(historyError.message);return}setEvents((data??[]) as UserAccessEvent[])})},[userId,organizationId]);
  const labels:Record<string,string>={created:"Utilizador criado",updated:"Perfil atualizado",role_changed:"Função alterada",permissions_changed:"Permissões alteradas",suspended:"Acesso suspenso",reactivated:"Acesso reativado",password_reset_required:"Palavra-passe temporária definida",access_removed:"Acesso removido"};
  return <section className="user-history"><header><div><strong>Histórico de alterações</strong><small>Registo cronológico e imutável deste utilizador.</small></div><span>{events.length}</span></header>{loading?<p>A carregar histórico…</p>:error?<p className="form-error">{error}</p>:events.length===0?<p>Ainda não existem alterações registadas.</p>:<div>{events.map(event=><article key={`${event.scope}-${event.id}`}><i/><span><strong>{labels[event.event_type]??event.event_type}</strong><small>{event.message||"Alteração administrativa registada."}</small><em>{event.actor_display_name} · {new Date(event.created_at).toLocaleString("pt-PT")}</em></span></article>)}</div>}</section>;
}

function EditUserDialog({
  user,
  isSuperMaster,
  onClose,
  onSaved,
}: {
  user: PlatformUser;
  isSuperMaster: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [role, setRole] = useState(user.role_key),
    [permissions, setPermissions] = useState(user.permissions ?? []),
    [active, setActive] = useState(user.active),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [confirmDelete, setConfirmDelete] = useState(false);
  const locked =
    user.role_key === "super_master" ||
    (!isSuperMaster && user.role_key === "platform_master");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (locked) return;
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const { error } = await supabase.functions.invoke("manage-platform-user", {
      body: {
        action: "update_platform_user",
        user_id: user.user_id,
        full_name: form.get("full_name"),
        personal_email: form.get("personal_email"),
        professional_email: form.get("professional_email"),
        system_name: form.get("system_name"),
        phone: form.get("phone"),
        role_key: role,
        permissions,
        active,
      },
    });
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    onSaved("Acesso do utilizador atualizado.");
  };
  return (
    <UserDialog
      title={user.display_name ?? "Utilizador"}
      subtitle={`${user.email ?? "Email não indicado"} · ${roleLabels[user.role_key]}`}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="identity-sections">
          <section>
            <header><strong>Informação pessoal</strong><small>Dados pessoais do funcionário.</small></header>
            <div className="form-grid">
              <label><span>Nome completo *</span><input name="full_name" required defaultValue={user.full_name ?? ""} disabled={locked}/></label>
              <label><span>Email pessoal</span><input name="personal_email" type="email" defaultValue={user.personal_email ?? ""} disabled={locked}/></label>
              <label><span>Telefone</span><input name="phone" type="tel" defaultValue={user.phone ?? ""} disabled={locked}/></label>
            </div>
          </section>
          <section>
            <header><strong>Informação profissional e acesso</strong><small>Nome apresentado no Stand e credenciais profissionais.</small></header>
            <div className="form-grid professional-access-grid">
              <label><span>Nome no sistema *</span><input name="system_name" required defaultValue={user.alias ?? user.display_name ?? ""} disabled={locked}/></label>
              <label><span>Email profissional / acesso *</span><input name="professional_email" type="email" required defaultValue={user.email ?? ""} disabled={locked}/></label>
              <label><span>Função</span><select value={role} disabled={locked} onChange={(event) => setRole(event.target.value as UserRole)}><option value="commercial">Comercial</option><option value="commercial_manager">Gestor Comercial</option><option value="platform_master">Master</option>{user.role_key === "super_master" && <option value="super_master">Super Master</option>}</select></label>
              <label className="access-switch"><input type="checkbox" checked={active} disabled={locked} onChange={(event) => setActive(event.target.checked)}/><span><strong>Acesso ativo</strong><small>Ao suspender, a pessoa deixa de ter acesso operacional.</small></span></label>
            </div>
          </section>
        </div>
        {role !== "commercial" && (
          <>
            <h3>Permissões</h3>
            <PermissionPicker
              value={permissions}
              onChange={locked ? () => {} : setPermissions}
            />
          </>
        )}
        {locked && (
          <p className="form-guidance">
            <strong>Acesso protegido</strong>
            <span>Este perfil não pode ser alterado por este utilizador.</span>
          </p>
        )}
        <PasswordResetControl
          userId={user.user_id}
          enabled={isSuperMaster || !locked}
          onSaved={onSaved}
        />
        <UserAccessHistory userId={user.user_id} />
        {!locked && !confirmDelete && (
          <div className="administrative-action-control">
            <strong>Eliminar utilizador</strong>
            <span>A eliminação só será aceite se a conta não possuir responsabilidades ou histórico operacional. Caso contrário, suspenda o acesso.</span>
            <button className="danger-button" type="button" onClick={() => setConfirmDelete(true)}>Eliminar conta</button>
          </div>
        )}
        {confirmDelete && (
          <div className="destructive-confirm">
            <strong>Eliminar definitivamente esta conta?</strong>
            <span>Esta ação não pode ser desfeita. O sistema recusará a eliminação se existirem dependências.</span>
            <div><button className="text-button" type="button" onClick={() => setConfirmDelete(false)}>Cancelar</button><button className="danger-button" type="button" disabled={saving} onClick={async()=>{setSaving(true);const {error:deleteError}=await supabase.functions.invoke("manage-platform-user",{body:{action:"delete_platform_user_account",user_id:user.user_id}});setSaving(false);if(deleteError){setError(deleteError.message);return}onSaved("Utilizador eliminado.")}}>Confirmar eliminação</button></div>
          </div>
        )}
        {error && <p className="form-error">{error}</p>}
        <footer>
          <button className="text-button" type="button" onClick={onClose}>
            Fechar
          </button>
          {!locked && (
            <button className="primary-button compact" disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          )}
        </footer>
      </form>
    </UserDialog>
  );
}
function EditStandUserDialog({
  user,
  canManage,
  onClose,
  onSaved,
}: {
  user: StandUser;
  canManage: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [role, setRole] = useState(user.role_key),
    [status, setStatus] = useState(user.status),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [confirm, setConfirm] = useState<"remove" | "delete" | null>(null);
  const invoke = async (body: Record<string, unknown>, success: string) => {
    setSaving(true);
    setError(null);
    const { error } = await supabase.functions.invoke("manage-platform-user", {
      body,
    });
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    onSaved(success);
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await invoke(
      {
        action: "update_stand_user",
        membership_id: user.membership_id,
        display_name: form.get("system_name"),
        system_name: form.get("system_name"),
        full_name: form.get("full_name"),
        personal_email: form.get("personal_email"),
        professional_email: form.get("professional_email"),
        phone: form.get("phone"),
        stand_role: role,
        status,
        status_reason: form.get("status_reason"),
      },
      "Acesso do utilizador atualizado.",
    );
  };
  return (
    <UserDialog
      title={user.display_name}
      subtitle={`${user.organization_name} · ${user.email ?? "Email não indicado"}`}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="identity-sections">
          <section><header><strong>Informação pessoal</strong><small>Identificação do funcionário do stand.</small></header><div className="form-grid"><label><span>Nome completo *</span><input name="full_name" required defaultValue={user.full_name??""} disabled={!canManage}/></label><label><span>Email pessoal</span><input name="personal_email" type="email" defaultValue={user.personal_email??""} disabled={!canManage}/></label><label><span>Telefone</span><input name="phone" type="tel" defaultValue={user.phone??""} disabled={!canManage}/></label></div></section>
          <section><header><strong>Informação profissional e acesso</strong><small>Identidade apresentada no Stand e credenciais do stand.</small></header><div className="form-grid professional-access-grid"><label><span>Nome no sistema *</span><input name="system_name" required defaultValue={user.alias??user.display_name} disabled={!canManage}/></label><label><span>Email profissional / acesso *</span><input name="professional_email" type="email" required defaultValue={user.email??""} disabled={!canManage}/></label><label><span>Função no stand</span><select value={role} disabled={!canManage} onChange={(event)=>setRole(event.target.value)}>{Object.entries(standRoleLabels).map(([key,label])=><option key={key} value={key}>{label}</option>)}</select></label><label><span>Estado do acesso</span><select value={status} disabled={!canManage} onChange={(event)=>setStatus(event.target.value)}><option value="active">Ativo</option><option value="suspended">Suspenso</option></select></label>{status!==user.status&&<label className="wide"><span>Motivo da alteração *</span><textarea name="status_reason" required rows={3}/></label>}</div></section>
        </div>
        <PasswordResetControl
          userId={user.user_id}
          membershipId={user.membership_id}
          enabled={canManage}
          onSaved={onSaved}
        />
        <UserAccessHistory userId={user.user_id} organizationId={user.organization_id}/>
        {canManage && !confirm && (
          <div className="administrative-action-control">
            <strong>Ações administrativas</strong>
            <span>
              Remover mantém a conta e o histórico. Eliminar é definitivo e só
              será permitido quando não existirem dependências.
            </span>
            <select
              value=""
              onChange={(event) =>
                setConfirm(event.target.value as "remove" | "delete")
              }
            >
              <option value="">Escolher uma ação</option>
              <option value="remove">Remover acesso deste stand</option>
              <option value="delete">Eliminar definitivamente a conta</option>
            </select>
          </div>
        )}
        {confirm && (
          <div className="destructive-confirm">
            <strong>
              {confirm === "remove"
                ? "Remover o acesso a este stand?"
                : "Eliminar definitivamente esta conta?"}
            </strong>
            <span>
              {confirm === "remove"
                ? "A conta e o histórico permanecem; apenas este vínculo é removido."
                : "Só será permitido se não existirem outros acessos ou histórico operacional. Esta ação não pode ser desfeita."}
            </span>
            <div>
              <button
                type="button"
                className="text-button"
                onClick={() => setConfirm(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="danger-button"
                disabled={saving}
                onClick={() =>
                  void invoke(
                    confirm === "remove"
                      ? {
                          action: "remove_stand_access",
                          membership_id: user.membership_id,
                        }
                      : {
                          action: "delete_stand_user_account",
                          user_id: user.user_id,
                          organization_id: user.organization_id,
                        },
                    confirm === "remove"
                      ? "Acesso removido do stand."
                      : "Conta eliminada definitivamente.",
                  )
                }
              >
                Confirmar
              </button>
            </div>
          </div>
        )}
        {error && <p className="form-error">{error}</p>}
        <footer>
          <button className="text-button" type="button" onClick={onClose}>
            Fechar
          </button>
          {canManage && (
            <button className="primary-button compact" disabled={saving}>
              {saving ? "A guardar…" : "Guardar"}
            </button>
          )}
        </footer>
      </form>
    </UserDialog>
  );
}
